import { createRoot } from "react-dom/client";
import { MedosClient } from "../../client/MedosClient";
export class BaseWidget {
    constructor() {
        this.root = null;
        this.container = null;
        this.config = null;
        this.isModalMode = false;
    }
    getIssuer() {
        return "booking-widget";
    }
    init(config) {
        if (!config.apiKey) {
            throw new Error("API key is required");
        }
        this.config = config;
        this.isModalMode = config.mode === "modal";
        if (config.fontUrl) {
            ensureFontStylesheet(config.fontUrl);
        }
        MedosClient.init({
            apiKey: config.apiKey,
            externalMemberId: config.externalMemberId,
            doctorInfo: config.doctorInfo,
            doctorInfoByDoctorId: config.doctorInfoByDoctorId,
            iss: this.getIssuer(),
        }).catch((error) => {
            console.error("Failed to initialize MedosClient:", error);
            if (config.onError) {
                config.onError(error);
            }
        });
        if (this.isModalMode) {
            this.openModal();
        }
        else {
            this.renderInline();
        }
    }
    open(config) {
        if (config) {
            this.config = { ...this.config, ...config };
        }
        if (!this.config?.apiKey) {
            throw new Error("API key is required. Call init() first or pass apiKey in config.");
        }
        this.isModalMode = true;
        this.openModal();
    }
    close() {
        if (this.container && this.isModalMode) {
            this.cleanup();
            if (this.config?.onClose) {
                this.config.onClose();
            }
        }
    }
    renderInline() {
        if (!this.config?.containerId) {
            throw new Error("containerId is required for inline mode");
        }
        const containerElement = document.getElementById(this.config.containerId);
        if (!containerElement) {
            throw new Error(`Container with id "${this.config.containerId}" not found`);
        }
        this.container = containerElement;
        this.render();
    }
    openModal() {
        const modalContainer = document.createElement("div");
        modalContainer.id = this.getModalId();
        modalContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 999999;
      padding: 20px;
      overflow-y: auto;
    `;
        const maxWidthCss = cssSize(this.config?.maxWidth) ?? "600px";
        const maxHeightCss = cssSize(this.config?.maxHeight) ?? "90vh";
        const contentWrapper = document.createElement("div");
        contentWrapper.style.cssText = `
      background: white;
      border-radius: 12px;
      max-width: ${maxWidthCss};
      width: 100%;
      max-height: ${maxHeightCss};
      overflow-y: auto;
      position: relative;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    `;
        const closeButton = document.createElement("button");
        closeButton.innerHTML = "×";
        closeButton.style.cssText = `
      position: absolute;
      top: 12px;
      right: 12px;
      background: transparent;
      border: none;
      font-size: 32px;
      cursor: pointer;
      color: #666;
      z-index: 1000000;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s;
    `;
        closeButton.onmouseover = () => {
            closeButton.style.background = "#f0f0f0";
            closeButton.style.color = "#333";
        };
        closeButton.onmouseout = () => {
            closeButton.style.background = "transparent";
            closeButton.style.color = "#666";
        };
        closeButton.onclick = () => this.close();
        const widgetContainer = document.createElement("div");
        widgetContainer.id = this.getWidgetContentId();
        contentWrapper.appendChild(closeButton);
        contentWrapper.appendChild(widgetContainer);
        modalContainer.appendChild(contentWrapper);
        document.body.appendChild(modalContainer);
        this.container = widgetContainer;
        modalContainer.onclick = (e) => {
            if (e.target === modalContainer) {
                this.close();
            }
        };
        this.render();
    }
    render() {
        if (!this.container || !this.config) {
            return;
        }
        this.root = createRoot(this.container);
        this.renderComponent();
    }
    reset() {
        if (this.root) {
            this.root.unmount();
            this.root = null;
        }
        if (this.isModalMode) {
            const modal = document.getElementById(this.getModalId());
            if (modal)
                modal.remove();
        }
        this.container = null;
        this.config = null;
        this.isModalMode = false;
    }
    cleanup() {
        if (this.root) {
            this.root.unmount();
            this.root = null;
        }
        if (this.isModalMode) {
            const modal = document.getElementById(this.getModalId());
            if (modal) {
                modal.remove();
            }
        }
        this.container = null;
    }
}
export function cssSize(value) {
    if (value === undefined || value === null || value === "")
        return undefined;
    return typeof value === "number" ? `${value}px` : value;
}
export function ensureFontStylesheet(url) {
    if (typeof document === "undefined" || !url)
        return;
    const existing = document.querySelector(`link[data-medos-font="${CSS.escape(url)}"]`);
    if (existing)
        return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    link.setAttribute("data-medos-font", url);
    document.head.appendChild(link);
}
